import { EmptyState, FlexBox, Text } from '@dixa/lib-shared-habla';
import { colors, sizes, typography } from '@dixa/lib-shared-tokens';
import type {
  ChartInstrument as ChartInstrumentT,
  InstrumentDataGranularity,
  InstrumentPanel,
} from 'lib-analytics-types';
import { ComponentProps, Fragment, memo, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useInstrumentComparison } from '../../comparison/instrumentComparison.context';

import styles from './chartInstrument.module.scss';
import { ChartInstrumentGranularity } from './components/granularity/chartInstrumentGranularity';
import { ChartInstrumentTooltip } from './components/tooltip/chartInstrumentTooltip';
import { ChartInstrumentVisualization } from './components/visualization/chartInstrumentVisualization';
import { useChartInstrumentAxes } from './components/wrappers/useChartInstrumentAxes/useChartInstrumentAxes';
import { useChartInstrumentLegend } from './components/wrappers/useChartInstrumentLegend/useChartInstrumentLegend';
import { useChartInstrumentSeries } from './components/wrappers/useChartInstrumentSeries/useChartInstrumentSeries';
import { visualizationIcons } from './utils/appearance';
import { transformDataForRecharts } from './utils/dataUtils';

export type ChartInstrumentProps = {
  /** The chart instrument. */
  instrument: ChartInstrumentT;
  /** The containing panel. */
  panel: InstrumentPanel;
  /** Callback for when the granularity is changed. */
  onChangeGranularity?: (granularity: InstrumentDataGranularity) => void;
};

export const ChartInstrument = memo(function ChartInstrument({
  instrument,
  panel,
  onChangeGranularity,
}: ChartInstrumentProps): JSX.Element | null {
  const comparison = useInstrumentComparison();

  const [visualization, setVisualization] = useState(instrument.visualization);
  const [hoveredCategory, setHoveredCategory] = useState<number | null>(null);

  const { data, layout, title } = instrument;
  const isHorizontal = layout === 'horizontal';
  const isVertical = layout === 'vertical';

  const rechartsData = useMemo(
    () => transformDataForRecharts(data, { omitZero: visualization === 'bar' }),
    [data, visualization],
  );

  const granularity = useMemo(
    () => data.granularities?.find(({ selected }) => selected)?.value,
    [data.granularities],
  );

  const { xAxisProps, yAxisProps } = useChartInstrumentAxes({
    instrument,
    granularity,
  });

  const { legend, legendProps, hoveredLegend } = useChartInstrumentLegend({
    instrument,
    visualization,
  });

  const { getSeriesProps } = useChartInstrumentSeries({
    instrument,
    legend,
    hoveredLegend,
    hoveredCategory: hoveredCategory,
  });

  useUpdateEffect(() => {
    setVisualization(instrument.visualization);
  }, [instrument.visualization]);

  /**
   * Handles setting the hovered category when the mouse moves over the chart.
   *
   * The hovered category is only updated when there is at least one data point
   * for it in the chart. This is to prevent unnecessary re-renders when the
   * mouse moves over categories with no data to display in the tooltip.
   */
  const handleMouseMove = (state?: {
    isTooltipActive?: boolean;
    activeTooltipIndex?: number;
  }): void => {
    if (!state?.isTooltipActive) {
      setHoveredCategory(null);
      return;
    }

    const index = state.activeTooltipIndex;
    if (typeof index !== 'number') return;
    const hasData = Object.values(rechartsData[index] ?? {}).length > 1;
    setHoveredCategory(hasData ? index : null);
  };

  const handleMouseLeave = (): void => {
    setHoveredCategory(null);
  };

  const renderChartSeries = (): JSX.Element[] | null => {
    if (visualization === 'line') {
      const lines = getSeriesProps(visualization);

      return lines.map((lineProps, index) => (
        <Line key={index} {...lineProps} />
      ));
    }

    if (visualization === 'bar') {
      const bars = getSeriesProps(visualization);

      return bars.map((barProps, index) => <Bar key={index} {...barProps} />);
    }

    return null;
  };

  const renderChart = (): JSX.Element => {
    const legendLayout = data.options?.legend?.layout;
    const chartMargin = parseInt(sizes.spacer[300]);

    const defaultChartProps: ComponentProps<typeof BarChart> = {
      layout,
      syncId: comparison?.syncOptions?.chart?.tooltip ? panel.id : undefined,
      data: rechartsData,
      margin: {
        top: isHorizontal ? chartMargin : 0,
        right: isVertical || legendLayout === 'vertical' ? chartMargin : 0,
        bottom: legendLayout === 'horizontal' ? chartMargin / 2 : 0,
        left: 0,
      },
    };

    const chartChildren: JSX.Element = (
      <>
        <XAxis {...xAxisProps} />
        <YAxis {...yAxisProps} />

        {data.options?.cartesian && (
          <CartesianGrid
            vertical={isVertical}
            horizontal={isHorizontal}
            stroke={colors.neutral['200']}
          />
        )}

        {data.references?.map(({ value, label }, index: number) => (
          <ReferenceLine
            key={index}
            {...(isHorizontal ? { y: value } : { x: value })}
            stroke={colors.neutral['500']}
            strokeDasharray="5 5"
            isFront
          >
            <Label
              value={label}
              offset={8}
              fontSize={typography.size['200']}
              position={isHorizontal ? 'insideBottomLeft' : 'insideTopLeft'}
            />
          </ReferenceLine>
        ))}

        {data.options?.legend && <Legend {...legendProps} />}

        {data.options?.tooltip && (
          <Tooltip<number, string>
            content={(tooltipProps) => (
              <ChartInstrumentTooltip
                instrument={instrument}
                granularity={granularity}
                tooltipProps={tooltipProps}
              />
            )}
            cursor={{ fill: 'transparent' }}
          />
        )}

        {renderChartSeries()}
      </>
    );

    switch (visualization) {
      case 'bar':
        return (
          <BarChart
            {...defaultChartProps}
            barGap="1"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {chartChildren}
          </BarChart>
        );
      case 'line':
        return <LineChart {...defaultChartProps}>{chartChildren}</LineChart>;
    }
  };

  if (!data.categories.length || !data.series.length) {
    return (
      <EmptyState
        title="No data available"
        description="There is no data available for the selected period and filters."
        icon={visualizationIcons[visualization]}
      />
    );
  }

  const actions: (JSX.Element | null)[] = [
    data.granularities && data.granularities.length > 1 ? (
      <ChartInstrumentGranularity
        panel={panel}
        granularities={data.granularities}
        onChangeGranularity={onChangeGranularity}
      />
    ) : null,

    data.options?.visualizations ? (
      <ChartInstrumentVisualization
        panel={panel}
        visualization={visualization}
        onChangeVisualization={setVisualization}
      />
    ) : null,
  ];
  const showActions = actions.some(Boolean);

  return (
    <>
      {title && (
        <div>
          <Text.Heading variant="heading-4">{title}</Text.Heading>
        </div>
      )}

      {showActions && (
        <FlexBox alignSelf="flex-end" gap="300">
          {actions.map((action, index) => (
            <Fragment key={index}>{action}</Fragment>
          ))}
        </FlexBox>
      )}

      <ResponsiveContainer
        className={styles.container}
        width="100%"
        height="100%"
        debounce={100}
      >
        {renderChart()}
      </ResponsiveContainer>
    </>
  );
});
